
import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { acceptAgencyInviteApi, disconnectInstagramApi, fetchCreators, filterCreatorsApi, filterCreatorsCatalogueApi, getCreatorCampaigns, getFavouriteCreatorsApi, getSingleCreatorApi, rejectAgencyInviteApi, sendCollaborationRequest } from 'api/creatorApi';
import { setCreatorsCollaborationData, setInstagramData, setInstagramSuccessFalse, setReviewsReceived, setSingleCreatorData } from 'states/creatorDetails/creatorDetailsSlice';
import { setCreators } from 'states/creators/creatorSlice';

// Action to filter creators based on criteria
export const FilterCreatorsCatalogue = createAsyncThunk(
    "filter/creators",
    async ({ token, data, dropdownOpen, page }, { rejectWithValue }) => {
        try {
            // Call the API function to filter creators
            const response = await filterCreatorsApi(token, data, page);
            dropdownOpen(); // Trigger dropdown open after fetching

            return response; // Return API response∂
        } catch (error) {
            // Handle errors and return custom error messages from backend
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            } else {
                return rejectWithValue(error);
            }
        }
    }
);

// Action to get the user's favorite creators
export const GetFavouriteCreatorsApi = createAsyncThunk(
    "favourite/creators",
    async ({ token, page }, { rejectWithValue }) => {
        try {
            // Call the API function to fetch favorite creators
            const response = await getFavouriteCreatorsApi(token, page);
            return response; // Return API response
        } catch (error) {
            // Handle errors and return custom error messages from backend
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            } else {
                return rejectWithValue(error);
            }
        }
    }
);

// Action to filter creators' catalogue
export const filterCreatorsCatalogueCall = createAsyncThunk(
    'filter/creators',
    async ({ token, data, page }, { rejectWithValue }) => {
        try {
            const response = await filterCreatorsCatalogueApi(token, data, page);
            return response;
        } catch (error) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            } else {
                return rejectWithValue(error);
            }
        }
    }
);

// Fetch a single creator's details and dispatch data to Redux store
export const getSingleCreatorCall = createAsyncThunk(
    'creatorDetails/getSingleCreator',
    async ({ token, data }, { rejectWithValue, dispatch }) => {
        try {
            const response = await getSingleCreatorApi(token, data); // Using the API function
            dispatch(setCreatorsCollaborationData(response?.data?.collaborations));
            dispatch(setSingleCreatorData(response?.data?.data));
            dispatch(setReviewsReceived(response?.data?.data?.reviews));
            dispatch(setInstagramData(response?.data?.data?.user?.instagram_data));
            return response?.data;
        } catch (error) {
            // Handle error and reject
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            } else {
                return rejectWithValue(error);
            }
        }
    }
);

// Get Creator Campaigns
export const getCreatorCampaignsCall = createAsyncThunk(
    "filter/campaigns",
    async ({ header, formData, page }, { rejectWithValue }) => {
        try {
            const response = await getCreatorCampaigns(header, formData, page); // Call the API function
            return response; // Return the response data
        } catch (error) {
            // If error occurs, check for error response and return it, or just return the error
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            } else {
                return rejectWithValue(error);
            }
        }
    }
);

// Fetch creators (async thunk)
export const getCreators = createAsyncThunk(
    "creators/getCreators",
    async ({ token, dropdownOpen, creatorsCurrentPage }, { rejectWithValue, dispatch }) => {
        try {
            const response = await fetchCreators({ token, creatorsCurrentPage });
            dropdownOpen(); // Execute dropdownOpen callback after data is fetched
            dispatch(setCreators(response?.data)); // Assuming `data` contains the creators list
            return response;
        } catch (error) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data); // Return error from backend if present
            } else {
                return rejectWithValue(error.message || "An unknown error occurred");
            }
        }
    }
);

// Create AsyncThunk for sending a collaboration request
export const sendCollaborationCall = createAsyncThunk(
    "initeCreatorCall/inviteCreator", // Action type
    async ({ token, data }, { rejectWithValue, dispatch }) => {
        try {
            const response = await sendCollaborationRequest(token, data); // Call the API function

            // Optionally, you can dispatch other actions or show success messages
            // toast.success("Collaboration request sent successfully!");

            return response; // Return the response data
        } catch (error) {
            // Show an error toast message if the request fails
            // toast.error("Failed to send collaboration request, please try again later!");

            // Check for error response and reject with custom error message
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            } else {
                return rejectWithValue(error);
            }
        }
    }
);

// Accept Agency Invite
export const AcceptAgencyInviteCall = createAsyncThunk(
    "agency/invite/accept",
    async ({ data, newTabUrl }, { rejectWithValue }) => {
        try {
            const response = await acceptAgencyInviteApi(data);

            toast.success(response.data.message, {
                autoClose: 1000,
                onClose: () => {
                    window.open(newTabUrl, "_blank");
                },
            });

            return response;
        } catch (error) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            } else {
                return rejectWithValue(error);
            }
        }
    }
);

// Reject Agency Invite
export const RejectAgencyInviteCall = createAsyncThunk(
    "agency/invite/reject",
    async ({ data, newTabUrl }, { rejectWithValue }) => {
        try {
            const response = await rejectAgencyInviteApi(data);

            toast.success(response.data.message, {
                autoClose: 1000,
                onClose: () => {
                    window.open(newTabUrl, "_blank");
                },
            });

            return response;
        } catch (error) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            } else {
                return rejectWithValue(error);
            }
        }
    }
);

// Disconnect Instagram
export const disconnectInstagramCall = createAsyncThunk(
    "disconnect/disconnectInstagram",
    async ({ token }, { rejectWithValue, dispatch }) => {
        try {
            const response = await disconnectInstagramApi(token);

            setTimeout(() => {
                dispatch(setInstagramData(null));
                dispatch(setInstagramSuccessFalse(true))
            }, 2000);

            return response;
        } catch (error) {
            // return custom error message from backend if present
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            } else {
                return rejectWithValue(error);
            }
        }
    }
);

